import { bearer } from "./accessToken";
import { apiFetch } from "./apiFetch";
/**
 * `GET /organizations` — the orgs the signed-in user belongs to.
 *
 * The web calls api-v2 directly rather than through the agent (Corrections #3):
 * the renderer owns the Supabase session, so it is the only thing holding a
 * token, and routing an org list through a local process would mean handing that
 * token to something that has no other reason to hold it.
 *
 * **The path has no trailing slash.** api-v2 mounts this router at
 * `{API_V2_STR}/organizations` and declares the route as `@router.get("")`, so
 * the endpoint is exactly `/api/v2/organizations`. `VITE_API_BASE_URL` already
 * carries the `/api/v2` suffix (Corrections #17) — appending it again is the 404
 * that reads as a routing bug.
 *
 * The response is an **envelope**, `{ success, data }`, not a bare array. Item P
 * ports the full error taxonomy for the agent; this stays thin deliberately —
 * one endpoint, and the two failures a shell layout can actually act on.
 */

export interface OrganizationSubscription {
  plan_name: string;
  seat_count: number;
  /** 1 = active, 0 = inactive — an int, not a bool, in api-v2's schema. */
  status: number;
  /**
   * The window, **stored and returned as UTC** — api-v2 surfaces these the way
   * `UsageService` surfaces timestamps, so they are converted for display rather
   * than treated as already-local. Read by the Billing & Plan tab (#64): the
   * period bar and the renewal date are the only warning a user gets before the
   * shell's expiry gate (#63) meets them.
   */
  starts_at: string;
  ends_at: string;
  /** The plan's IANA zone (e.g. "Asia/Taipei") — the dates are formatted in it. */
  timezone: string;
}

export interface Organization {
  id: string;
  name: string;
  description: string | null;
  icon_url: string | null;
  active_subscription: OrganizationSubscription | null;
}

export type OrganizationsFailure =
  | { kind: "unauthenticated" }
  | { kind: "http"; status: number; detail: string | null }
  | { kind: "network"; detail: string };

export class OrganizationsError extends Error {
  readonly failure: OrganizationsFailure;
  constructor(failure: OrganizationsFailure, message: string) {
    super(message);
    this.name = "OrganizationsError";
    this.failure = failure;
  }
}

/**
 * FastAPI's `detail` comes back in two shapes — a bare string, and a
 * `{reason, message}` object — and which one you get depends on the raiser, not
 * on the status. Reading `detail` as a string turns the object shape into
 * `[object Object]` on screen.
 */
export function readDetail(body: unknown): string | null {
  if (typeof body !== "object" || body === null) return null;
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail === "string") return detail;
  if (typeof detail === "object" && detail !== null) {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === "string") return message;
  }
  return null;
}

export function organizationsUrl(apiBaseUrl: string): string {
  return `${apiBaseUrl.replace(/\/+$/, "")}/organizations`;
}

export async function fetchOrganizations(
  token: string | null,
  deps: { apiBaseUrl: string; fetchImpl?: typeof fetch },
): Promise<Organization[]> {
  // Not an HTTP concern: without a token there is nothing to ask, and asking
  // anyway returns a 401 that looks like an expired session.
  if (!token) throw new OrganizationsError({ kind: "unauthenticated" }, "not signed in");

  const fetchImpl = deps.fetchImpl ?? apiFetch;
  let response: Response;
  try {
    response = await fetchImpl(organizationsUrl(deps.apiBaseUrl), {
      headers: { Authorization: bearer(token) },
    });
  } catch (error) {
    throw new OrganizationsError({ kind: "network", detail: String(error) }, String(error));
  }

  if (response.status === 401 || response.status === 403) {
    throw new OrganizationsError({ kind: "unauthenticated" }, "session rejected by the API");
  }

  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const detail = readDetail(body);
    throw new OrganizationsError(
      { kind: "http", status: response.status, detail },
      detail ?? `HTTP ${response.status}`,
    );
  }

  const data = (body as { data?: unknown } | null)?.data;
  return Array.isArray(data) ? (data as Organization[]) : [];
}
